import { actions } from "./slice";
import { db } from "initializer";
import sendEvent from "modules/sendEvent";
import shortid from "shortid";
import {
  createSubscribeCollection,
  createSubscribeDocument,
} from "../firestoreModuleUtils/operators";
import { appStateMutate } from "../app.state/operations";
import { Room, Marker, UpdateRoom, UpdateMarker } from "./records";
import { RoomRecord } from "./records";
import { DefaultRootState } from "stores";
import { createSnapshot, duplicateRoomFunc } from "api";
import { DefaultThunk } from "stores";
import { getUid } from "../app.user/selectors";
import {
  getCurrentRoom,
  getMaxZIndexInMarkers,
  getRoomById,
  getRoomMarkerById,
  getRoomMarkerExists,
  getSelectedObjects,
  getSelectingObjects,
} from "./selectors";
import { MarkerRecord, MessageGroup } from "./records";
import mesureImage from "modules/mesureImage";
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  DocumentData,
  FirestoreDataConverter,
  query,
  where,
  writeBatch,
  deleteField,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { produce } from "immer";
import { addUndo } from "../entities.room.histories/slice";
import i18next from "i18next";
import { getSoundUrl, sendSoundEventLog } from "api-sound-master";
import { getAppState, getIsOwner } from "../app.state/selectors";
import { itemsRef } from "../entities.room.items/operations";
import { SelectedDragItem } from "containers/DraggableContext";
import {
  addRoomDeckItemsById,
  decksRef,
} from "../entities.room.decks/operations";
import { charactersRef } from "../entities.room.characters/operations";
import { dicesRef } from "../entities.room.dices/operations";
import { SelectedObject } from "../app.state";
import { UpdateObject } from "../entities.room.histories/records/updateFieldObjects";
import { Size } from "containers/Screen/Screen/SelectingArea";
import { nanoid } from "@reduxjs/toolkit";
import { encodeUnicodeToBase64 } from "modules/base64";
import {
  MarkerClipboardData,
  deleteAllMarkerIDB,
  writeMarkerIDB,
  getMarkerByKeyIDB,
} from "modules/markerIDB";
import { getRoomDeckIdByPosition } from "../entities.room.decks/selectors";
import { applyItems, updateTransform } from "../entities.room.transform/slice";

const roomConverter: FirestoreDataConverter<DocumentData> = {
  toFirestore(room: UpdateRoom): DocumentData {
    return room;
  },
  fromFirestore(snapshot, options): DocumentData {
    const data = snapshot.data(options)!;
    return RoomRecord(data);
  },
};

export const roomsRef = collection(db, "rooms").withConverter(roomConverter);
const keysRef = collection(db, "keys"); // todo

// Check if signed URL will expire within the specified seconds (default: 1 hour = 3600 seconds)
export const isUrlExpiringSoon = (
  url: string | null,
  secondsThreshold: number = 3600
): boolean => {
  if (!url) return true;

  try {
    const urlObj = new URL(url);
    const expiresParam = urlObj.searchParams.get("X-Goog-Expires");
    const dateParam = urlObj.searchParams.get("X-Goog-Date");

    if (!expiresParam || !dateParam) {
      // If we can't determine expiration, assume it needs refresh
      return true;
    }

    // Parse the date: format is YYYYMMDDTHHmmssZ
    const year = parseInt(dateParam.substring(0, 4), 10);
    const month = parseInt(dateParam.substring(4, 6), 10) - 1; // months are 0-indexed
    const day = parseInt(dateParam.substring(6, 8), 10);
    const hour = parseInt(dateParam.substring(9, 11), 10);
    const minute = parseInt(dateParam.substring(11, 13), 10);
    const second = parseInt(dateParam.substring(13, 15), 10);

    const issueDate = new Date(
      Date.UTC(year, month, day, hour, minute, second)
    );
    const expiresInSeconds = parseInt(expiresParam, 10);
    const expirationDate = new Date(
      issueDate.getTime() + expiresInSeconds * 1000
    );

    const now = new Date();
    const timeUntilExpiration = expirationDate.getTime() - now.getTime();
    const secondsUntilExpiration = timeUntilExpiration / 1000;

    return secondsUntilExpiration <= secondsThreshold;
  } catch (error) {
    // If there's any error parsing the URL, assume it needs refresh
    return true;
  }
};

// Helper function to get error message for SoundMaster errors
export const displaySoundMasterErrorMessage =
  (): DefaultThunk => (dispatch, getState) => {
    const state = getState();
    const showedSoundMasterErrorRoom = getAppState(
      state,
      "showedSoundMasterErrorRoom"
    );

    if (!showedSoundMasterErrorRoom) {
      dispatch(
        appStateMutate((state) => {
          state.errorSnackbarMessage = i18next.t(
            "再生にサウンドマスタープランへの加入が必要な音源が設定されています。"
          );
          state.showedSoundMasterErrorRoom = true;
        })
      );
    }
    return;
  };

// Helper function to resolve URLs from Refs
export const resolveUrlsFromRefs = async (
  data: UpdateRoom,
  current?: Room | null,
  token?: string
): Promise<{ resolved: UpdateRoom; error?: string }> => {
  const resolved = { ...data };
  let error: string | undefined;

  // Resolve mediaUrl from mediaRef if mediaRef is provided in data
  if (data.mediaRef) {
    // mediaRef has a value, check if it needs URL refresh
    if (data.mediaRef === current?.mediaRef) {
      // Same Ref: only fetch new URL if current URL is expiring soon
      if (isUrlExpiringSoon(current?.mediaUrl)) {
        const url = await getSoundUrl(data.mediaRef, token);
        if (url) {
          resolved.mediaUrl = url;
        } else if (!error) {
          error = "failed_to_get_soundmaster_url";
        }
      } else {
        // Keep the current URL if it's still valid
        resolved.mediaUrl = current.mediaUrl;
      }
    } else {
      // Different Ref: check if data already has a valid URL
      if (data.mediaUrl && !isUrlExpiringSoon(data.mediaUrl)) {
        // data.mediaUrl is valid, use it as-is
        resolved.mediaUrl = data.mediaUrl;
        // Send event log for new sound
        sendSoundEventLog("play", data.mediaRef);
      } else {
        // Fetch new URL if data.mediaUrl is missing or expiring soon
        const url = await getSoundUrl(data.mediaRef, token);
        if (url) {
          resolved.mediaUrl = url;
          // Send event log for new sound only if URL was successfully fetched
          sendSoundEventLog("play", data.mediaRef);
        } else if (!error) {
          error = "failed_to_get_soundmaster_url";
        }
      }
    }
  }

  // Resolve soundUrl from soundRef if soundRef is provided in data
  if (data.soundRef) {
    // soundRef has a value, check if it needs URL refresh
    if (data.soundRef === current?.soundRef) {
      // Same Ref: only fetch new URL if current URL is expiring soon
      if (isUrlExpiringSoon(current?.soundUrl)) {
        const url = await getSoundUrl(data.soundRef, token);
        if (url) {
          resolved.soundUrl = url;
        } else if (!error) {
          error = "failed_to_get_soundmaster_url";
        }
      } else {
        // Keep the current URL if it's still valid
        resolved.soundUrl = current.soundUrl;
      }
    } else {
      // Different Ref: check if data already has a valid URL
      if (data.soundUrl && !isUrlExpiringSoon(data.soundUrl)) {
        // data.soundUrl is valid, use it as-is
        resolved.soundUrl = data.soundUrl;
        // Send event log for new sound
        sendSoundEventLog("play", data.soundRef);
      } else {
        // Fetch new URL if data.soundUrl is missing or expiring soon
        const url = await getSoundUrl(data.soundRef, token);
        if (url) {
          resolved.soundUrl = url;
          // Send event log for new sound only if URL was successfully fetched
          sendSoundEventLog("play", data.soundRef);
        } else if (!error) {
          error = "failed_to_get_soundmaster_url";
        }
      }
    }
  }

  return { resolved, error };
};

export const subscribeUserRooms = createSubscribeCollection(
  actions,
  (_: unknown, state: DefaultRootState) => {
    const uid = getUid(state);
    if (!uid) return null;
    return query(
      roomsRef,
      where("owner", "==", uid),
      where("archived", "==", false)
    );
  }
);
export const subscribeRoom = createSubscribeDocument(actions, (id: string) =>
  doc(roomsRef, id)
);
export const addRoomWithRoomSettings =
  (): DefaultThunk => (dispatch, getState) => {
    sendEvent("addRoom");
    const batch = writeBatch(db);
    const id = shortid.generate();
    const state = getState();
    const uid = getUid(state);
    if (!uid) return Promise.reject();
    // todo dupricate check
    dispatch(
      appStateMutate((state) => {
        state.roomId = id;
        // Homeコンポーネント内のRoomSettingsでroomIdを使うために設定
        state.openRoomSettings = true;
      })
    );
    batch.set(
      doc(roomsRef, id),
      RoomRecord({
        owner: uid,
        diceBotName: "DiceBot",
        diceBotSystem: "DiceBot",
      })
    );
    batch.set(doc(keysRef, id), {
      keys: {
        master: shortid.generate(),
        player: shortid.generate(),
        audience: shortid.generate(),
      },
      owner: uid,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    return batch.commit();
  };
export const updateRoom =
  (id: string, data: UpdateRoom): DefaultThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const currentRoom = getRoomById(getState(), id);
    const result = await resolveUrlsFromRefs(
      data,
      currentRoom,
      currentRoom?.soundMasterToken || undefined
    );

    // Show snackbar if there was an error
    if (result.error) {
      dispatch(displaySoundMasterErrorMessage());
    }

    return updateDoc(doc(roomsRef, id), {
      updatedAt: Date.now(),
      ...result.resolved,
    });
  };

export const updateCurrentRoomFeature =
  (name: keyof Room["features"], flag: boolean): DefaultThunk =>
  (dispatch, getState) => {
    const room = getCurrentRoom(getState());
    if (!room) return;
    dispatch(
      updateCurrentRoom({ features: { ...room.features, [name]: flag } })
    );
  };

export const updateCurrentRoom =
  (data: UpdateRoom): DefaultThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const roomId = getState().app.state.roomId;
    if (!roomId) return;
    const currentRoom = getCurrentRoom(getState());
    const result = await resolveUrlsFromRefs(
      data,
      currentRoom,
      currentRoom?.soundMasterToken || undefined
    );

    // Show snackbar if there was an error
    if (result.error) {
      // Sound Masterエラーになる可能性があるのはzip importとoverroid preset読み込み時のみ
      dispatch(displaySoundMasterErrorMessage());
    }

    await updateDoc(doc(roomsRef, roomId), {
      updatedAt: Date.now(),
      ...result.resolved,
    });
  };

export const updateCurrentRoomField =
  (data: UpdateRoom): DefaultThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const roomId = getState().app.state.roomId;
    if (!roomId) return;
    const room = getState().entities.rooms.entities[roomId];
    const result = await resolveUrlsFromRefs(
      data,
      room,
      room?.soundMasterToken || undefined
    );

    // Show snackbar if there was an error
    if (result.error) {
      dispatch(displaySoundMasterErrorMessage());
    }

    await updateDoc(doc(roomsRef, roomId), {
      updatedAt: Date.now(),
      ...result.resolved,
    });
    const isUpdate = Object.keys(result.resolved).some(
      (key) => result.resolved[key] !== room[key]
    );
    if (isUpdate) {
      dispatch(
        addUndo({
          kind: "update-field",
          id: roomId,
          before: room,
          after: { ...room, ...result.resolved },
        })
      );
    }
  };

type OptionsArchiveRoom = {
  eventTrigger?: string;
};

export const archiveRoom =
  (roomId: string, options?: OptionsArchiveRoom) => (_, getState) => {
    sendEvent("archiveRoom");
    const uid = getUid(getState());
    const batch = writeBatch(db);

    const eventTrigger = options?.eventTrigger || "some trigger";

    // ルーム削除処理が呼ばれたことをログに保存
    batch.set(doc(collection(db, "event_logs/archive_room/logs")), {
      uid,
      roomId,
      eventTrigger,
      archivedAt: Date.now(),
    });

    batch.set(
      doc(roomsRef, roomId),
      {
        updatedAt: Date.now(),
        archived: true,
      },
      { merge: true }
    );

    return batch.commit();
  };

export const updateRoomMarker =
  (roomId: string, markerId: string, data: UpdateMarker) => () => {
    return setDoc(
      doc(roomsRef, roomId),
      {
        markers: {
          [markerId]: {
            ...data,
          },
        },
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };

export const upsertRoomMarker =
  (roomId: string, markers: Record<string, Marker>) => () => {
    return updateDoc(doc(roomsRef, roomId), {
      markers,
      updatedAt: Date.now(),
    });
  };

export const updateCurrentRoomMarker =
  (data: UpdateMarker): DefaultThunk =>
  (dispatch, getState) => {
    const roomId = getState().app.state.roomId;
    const markerId = getState().app.state.openRoomMarkerDetailId;
    if (!roomId || !markerId) return;
    const marker = getRoomMarkerById(getState(), roomId, markerId);
    setDoc(
      doc(roomsRef, roomId),
      {
        markers: {
          [markerId]: {
            ...data,
          },
        },
        updatedAt: Date.now(),
      },
      { merge: true }
    );
    dispatch(
      addUndo({
        kind: "update-marker",
        id: markerId,
        before: marker,
        after: { ...marker, ...data },
      })
    );
    return;
  };

export const addUndoUpdateMarker =
  (roomId: string, markerId: string, data: UpdateMarker): DefaultThunk =>
  (dispatch, getState) => {
    const marker = getRoomMarkerById(getState(), roomId, markerId);
    dispatch(updateRoomMarker(roomId, markerId, data));
    dispatch(
      addUndo({
        kind: "update-marker",
        id: markerId,
        before: marker,
        after: { ...marker, ...data },
      })
    );
  };

export const addRoomMarker =
  (data: UpdateMarker): DefaultThunk =>
  async (dispatch, getState) => {
    const roomId = getState().app.state.roomId;
    if (!roomId) return;
    const maxZIndex = getMaxZIndexInMarkers(getState());
    const cellSize = 24; // todo
    if (data.imageUrl && !data.width && !data.height) {
      const image = await mesureImage(data.imageUrl, cellSize, {
        width: data.width || 2,
        height: data.height || 2,
      });
      data.width = image.width;
      data.height = image.height;
      data.x = Math.ceil(-image.width / 2);
      data.y = Math.ceil(-image.height / 2);
    }
    const newMarkerId = Date.now().toString(16);
    const newMarker = { ...data, z: maxZIndex };
    setDoc(
      doc(roomsRef, roomId),
      {
        markers: {
          [newMarkerId]: MarkerRecord(newMarker),
        },
        updatedAt: Date.now(),
      },
      { merge: true }
    );
    dispatch(
      addUndo({
        kind: "update-marker",
        id: newMarkerId,
        before: null,
        after: newMarker,
      })
    );
    return;
  };

export const cloneRoomMarker =
  (markerId): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId) return;
    const marker = getRoomMarkerById(state, roomId, markerId);
    const cloneMarkerId = Date.now().toString(16);
    setDoc(
      doc(roomsRef, roomId),
      {
        markers: {
          [cloneMarkerId]: MarkerRecord(marker),
        },
        updatedAt: Date.now(),
      },
      { merge: true }
    );
    dispatch(
      addUndo({
        kind: "update-marker",
        id: cloneMarkerId,
        before: null,
        after: marker,
      })
    );
    return;
  };

type MarkerCopied = {
  kind: "copiedMarker";
  data: string;
};

export const copyRoomMarkerToClipboard =
  (markerId: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId) return;
    const marker = getRoomMarkerById(state, roomId, markerId);
    if (!marker) return;

    const markerData: MarkerClipboardData = {
      id: markerId,
      roomId,
      x: marker.x,
      y: marker.y,
      width: marker.width,
      height: marker.height,
      locked: marker.locked,
      freezed: marker.freezed,
      imageUrl: marker.imageUrl,
      text: marker.text,
      angle: marker.angle,
      clickAction: marker.clickAction,
    };

    const buildHtmlBlob = async () => {
      await deleteAllMarkerIDB();
      const keyIDB = nanoid();
      await writeMarkerIDB({ key: keyIDB, copyData: markerData });
      const clipboardData: MarkerCopied = {
        kind: "copiedMarker",
        data: keyIDB,
      };
      const base64 = encodeUnicodeToBase64(JSON.stringify(clipboardData));
      const span = document.createElement("span");
      span.setAttribute("data-metadata", base64);
      return new Blob([span.outerHTML], { type: "text/html" });
    };

    const labelBlob = new Blob([marker.text || "マーカーパネル"], {
      type: "text/plain",
    });

    const clipboardItem = new ClipboardItem({
      "text/plain": labelBlob,
      "text/html": buildHtmlBlob(),
    });

    navigator.clipboard
      .write([clipboardItem])
      .then(() => {
        dispatch(
          appStateMutate(
            (state) =>
              (state.errorSnackbarMessage = i18next.t(
                "パネルデータをクリップボードにコピーしました。"
              ))
          )
        );
      })
      .catch(() =>
        alert(
          i18next.t(
            "エラー：パネルデータをクリップボードにコピーしようとしましたが、失敗しました。"
          )
        )
      );
  };

const parseClipboardTextIfMarker = (text: string): MarkerCopied | null => {
  try {
    const parsed = JSON.parse(text);
    return parsed.kind === "copiedMarker" ? (parsed as MarkerCopied) : null;
  } catch {
    return null;
  }
};

export const importRoomMarkerFromClipboardText =
  (text: string): DefaultThunk =>
  async (dispatch, getState) => {
    const clipboard = parseClipboardTextIfMarker(text);
    if (!clipboard) {
      alert(i18next.t("クリップボードからのデータ読み込みに失敗しました"));
      return;
    }
    const dbData = await getMarkerByKeyIDB(clipboard.data);
    if (!dbData || !dbData.copyData) {
      alert(i18next.t("クリップボードからのデータ読み込みに失敗しました"));
      return;
    }
    const markerData = dbData.copyData;
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId) return;

    if (markerData.roomId != null && markerData.roomId !== roomId) {
      dispatch(
        appStateMutate(
          (state) =>
            (state.errorSnackbarMessage = i18next.t(
              "このデータは別のルームでコピーされたため、貼り付けできません。"
            ))
        )
      );
      return;
    }

    // IDが存在し、かつそのIDがルームに存在しない場合は同じIDで貼り付け（シーントランジション維持）
    const originalId = markerData.id;
    const useOriginalId =
      originalId != null && !getRoomMarkerExists(state, roomId, originalId);
    const pasteMarkerId = useOriginalId ? originalId : Date.now().toString(16);

    const maxZIndex = getMaxZIndexInMarkers(state);
    const newMarker = MarkerRecord({
      ...markerData,
      x: markerData.x ?? Math.ceil(-markerData.width / 2),
      y: markerData.y ?? Math.ceil(-markerData.height / 2),
      z: maxZIndex,
    });

    await setDoc(
      doc(roomsRef, roomId),
      {
        markers: { [pasteMarkerId]: newMarker },
        updatedAt: Date.now(),
      },
      { merge: true }
    );
    dispatch(
      addUndo({
        kind: "update-marker",
        id: pasteMarkerId,
        before: null,
        after: newMarker,
      })
    );
  };

export const migrationMessageChannels = (): DefaultThunk => (_, getState) => {
  const state = getState();
  const room = getCurrentRoom(state);
  if (!room || room.messageChannels.length === 0) return;

  const messageGroups = room.messageChannels.map((channel): MessageGroup => {
    return {
      id: channel,
      name: channel,
      kind: "public",
    };
  });

  updateDoc(doc(roomsRef, room._id), {
    messageChannels: arrayRemove(...room.messageChannels),
    messageGroups: arrayUnion(...messageGroups),
    updatedAt: Date.now(),
  });
};

export const addRoomMessageGroup =
  (data: MessageGroup): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId) {
      return;
    }

    updateDoc(doc(roomsRef, roomId), {
      messageGroups: arrayUnion(data),
      updatedAt: Date.now(),
    });
  };

export const updateRoomMessageGroup =
  (data: MessageGroup): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    const room = getCurrentRoom(state);
    if (!roomId || !room) {
      return;
    }

    const idx = room.messageGroups.findIndex((group) => group.id === data.id);
    if (idx < 0) {
      return;
    }

    const messageGroups = produce(room.messageGroups, (draft) => {
      draft[idx] = data;
    });

    updateDoc(doc(roomsRef, roomId), {
      messageGroups,
      updatedAt: Date.now(),
    });
  };

export const removeRoomMessageGroup =
  (groupId: string): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    const room = getRoomById(state, roomId || "");
    if (!roomId || !room) return;

    const index = room.messageGroups.findIndex((group) => group.id === groupId);
    const group = room.messageGroups[index];

    if (!group) return;

    dispatch(
      appStateMutate((state) => {
        state.roomChatTab = "main";
      })
    );

    updateDoc(doc(roomsRef, roomId), {
      messageGroups: arrayRemove(group),
      updatedAt: Date.now(),
    });
  };

export const deleteRoomMarker = (id: string, markerId: string) => () => {
  return setDoc(
    doc(roomsRef, id),
    {
      markers: {
        [markerId]: deleteField(),
      },
      updatedAt: Date.now(),
    },
    { merge: true }
  );
};

export const saveInitialRoomState = async (roomId: string) => {
  const { snapshotId } = await createSnapshot(roomId);
  const roomDoc = await getDoc(doc(roomsRef, roomId));

  if (!roomDoc.exists()) {
    return;
  }

  const room = RoomRecord(roomDoc.data());
  const thumbnail = room.foregroundUrl;
  const data: UpdateRoom = {
    updatedAt: Date.now(),
    initialSavedata: { thumbnail, snapshotId },
  };
  await updateDoc(doc(roomsRef, roomId), data);
};

export const deleteRoom = (id: string) => () => {
  const batch = writeBatch(db);
  batch.delete(doc(roomsRef, id));
  batch.delete(doc(keysRef, id));
  return batch.commit();
};

export const duplicateRoom =
  (roomId: string): DefaultThunk =>
  () => {
    sendEvent("duplicateRoom");
    const subcollections = [
      "characters",
      "effects",
      "items",
      "scenes",
      "notes",
      "dices",
      "commands",
      "decks",
    ];
    return duplicateRoomFunc({
      roomId,
      subcollections,
    });
  };

export const loginRoom = (id: string) => () => {
  if (!id.match(/^[\w-]+$/)) {
    window.alert(i18next.t("存在しないルームIDです"));
    return;
  }

  return getDoc(doc(roomsRef, id)).then((doc) => {
    if (doc.exists()) {
      window.location.href = `/rooms/${id}`;
    } else {
      window.alert(i18next.t("存在しないルームIDです"));
    }
  });
};

export const loadSoundMasterUrl =
  (type: "media" | "sound"): DefaultThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const roomId = getState().app.state.roomId;
    if (!roomId) return;

    const state = getState();
    const isOwner = getIsOwner(state);
    const currentRoom = getCurrentRoom(state);
    const ref =
      type === "media" ? currentRoom?.mediaRef : currentRoom?.soundRef;
    const url =
      type === "media" ? currentRoom?.mediaUrl : currentRoom?.soundUrl;
    if (ref && (!url || isUrlExpiringSoon(url))) {
      if (isOwner) {
        const newUrl = await getSoundUrl(ref);
        if (newUrl) {
          const newData =
            type === "media"
              ? {
                  mediaUrl: newUrl,
                }
              : {
                  soundUrl: newUrl,
                };
          await updateDoc(doc(roomsRef, roomId), {
            ...newData,
            updatedAt: Date.now(),
          });
        } else {
          // URLを取得できなかった場合もメッセージを表示
          dispatch(displaySoundMasterErrorMessage());
        }
      }
    }
    return;
  };

export const updateObjects =
  (items: UpdateObject[], roomId: string) => async () => {
    const batch = writeBatch(db);

    items.forEach((item) => {
      switch (item.selectType) {
        case "item":
          batch.set(
            doc(itemsRef(roomId), item.id),
            {
              ...item.data,
              updatedAt: Date.now(),
            },
            { merge: true }
          );
          break;
        case "deck":
          batch.set(
            doc(decksRef(roomId), item.id),
            {
              ...item.data,
              updatedAt: Date.now(),
            },
            { merge: true }
          );
          break;
        case "marker":
          batch.set(
            doc(roomsRef, roomId),
            {
              markers: {
                [item.id]: {
                  ...item.data,
                },
              },
              updatedAt: Date.now(),
            },
            { merge: true }
          );
          break;
        case "character":
          batch.set(
            doc(charactersRef(roomId), item.id),
            {
              ...item.data,
              updatedAt: Date.now(),
            },
            { merge: true }
          );
          break;
        case "diceItem":
          batch.set(
            doc(dicesRef(roomId), item.id),
            {
              ...item.data,
              updatedAt: Date.now(),
            },
            { merge: true }
          );
          break;
      }
    });

    await batch.commit();
  };
export const deleteObjects =
  (items: SelectedObject[], roomId: string) => async () => {
    const batch = writeBatch(db);

    items.forEach((item) => {
      switch (item.selectType) {
        case "item":
          batch.delete(doc(itemsRef(roomId), item.id));
          break;
        case "deck":
          batch.delete(doc(decksRef(roomId), item.id));
          break;
        case "marker":
          batch.set(
            doc(roomsRef, roomId),
            {
              markers: {
                [item.id]: deleteField(),
              },
              updatedAt: Date.now(),
            },
            { merge: true }
          );
          break;
        case "character":
          batch.delete(doc(charactersRef(roomId), item.id));
          break;
        case "diceItem":
          batch.delete(doc(dicesRef(roomId), item.id));
          break;
      }
    });

    await batch.commit();
  };

export const moveRoomObjects =
  (
    objects: SelectedDragItem[],
    delta: { x: number; y: number }
  ): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId || objects.length === 0) return;
    const scale = getAppState(state, "roomScreenScale");
    const cellSize = getAppState(state, "roomScreenCellSize");
    const alignWithGrid = getCurrentRoom(state)?.alignWithGrid ?? true;
    let newX = delta.x / scale / cellSize;
    let newY = delta.y / scale / cellSize;
    const characters = objects.filter((o) => o.selectType === "character");
    const isOnlyItems =
      objects.filter((o) => o.selectType === "item").length === objects.length;

    const beforeItems: UpdateObject[] = [];
    const afterItems: UpdateObject[] = [];

    const updateItems = objects.map((item) => {
      let newPosition = {
        x: item.data.x + newX,
        y: item.data.y + newY,
      };
      if (item.selectType === "character") {
        if (alignWithGrid && characters.length !== objects.length) {
          newPosition = {
            x: item.data.x + Math.round(Math.round(newX) * cellSize),
            y: item.data.y + Math.round(Math.round(newY) * cellSize),
          };
        } else {
          newPosition = {
            x: item.data.x + Math.round(newX * cellSize),
            y: item.data.y + Math.round(newY * cellSize),
          };
        }
      } else if (alignWithGrid) {
        newPosition = {
          x: Math.round(item.data.x + newX),
          y: Math.round(item.data.y + newY),
        };
      }

      beforeItems.push({
        selectType: item.selectType,
        id: item.id,
        data: {
          x: item.data.x,
          y: item.data.y,
        },
      });

      afterItems.push({
        selectType: item.selectType,
        id: item.id,
        data: {
          ...newPosition,
        },
      });

      const itemSize =
        item.selectType === "item"
          ? {
              width: item.width,
              height: item.height,
              coverImageUrl: item.coverImageUrl,
            }
          : undefined;

      return {
        selectType: item.selectType,
        id: item.id,
        data: {
          ...newPosition,
        },
        itemSize,
      };
    });

    if (isOnlyItems && objects.length === 1) {
      const firstObject = updateItems[0];
      if (!firstObject) return;
      const firstItem = {
        x: firstObject.data.x,
        y: firstObject.data.y,
        height: firstObject.itemSize?.height || 0,
        width: firstObject.itemSize?.width || 0,
        coverImageUrl: firstObject.itemSize?.coverImageUrl,
      };

      const overlappedDeckId = getRoomDeckIdByPosition(state, firstItem);
      if (overlappedDeckId) {
        await dispatch(
          addRoomDeckItemsById(
            overlappedDeckId,
            objects.map((item) => item.id)
          )
        );
        dispatch(updateTransform(null));
        return;
      }
    }

    dispatch(updateObjects(updateItems, roomId));
    dispatch(applyItems(updateItems, roomId));

    dispatch(
      addUndo({
        kind: "update-field-objects",
        before: beforeItems,
        after: afterItems,
      })
    );
  };

export const deleteObjectsWithUndo =
  (items: SelectedObject[]): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const roomId = getAppState(state, "roomId");
    if (!roomId) return;

    const objects = getSelectedObjects(state, items);
    await dispatch(deleteObjects(items, roomId));
    dispatch(
      addUndo({
        kind: "update-field-objects",
        before: objects,
        after: null,
      })
    );
  };

export const selectRoomObjects =
  (
    selectingAreaSize: Size,
    centerX: number,
    centerY: number,
    isShift: boolean,
    startObjects: SelectedObject[]
  ): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const objects = getSelectingObjects(
      state,
      centerX,
      centerY,
      selectingAreaSize
    );

    if (!objects) return;

    if (!isShift) {
      dispatch(
        appStateMutate((state) => {
          state.selectedObjects = objects;
        })
      );
      return;
    }
    let newObjects = [...startObjects];
    objects.forEach((object) => {
      const fineded = newObjects.find(
        (o) => o.id === object.id && o.selectType === object.selectType
      );
      if (fineded) {
        newObjects = newObjects.filter(
          (o) => !(o.id === object.id && o.selectType === object.selectType)
        );
      } else {
        newObjects.push(object);
      }
    });
    dispatch(
      appStateMutate((state) => {
        state.selectedObjects = newObjects;
      })
    );
  };
